import { BreakpointObserver } from "@angular/cdk/layout";
import { Component, inject, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { UiKitModule } from "@app/ui-kit.module";
import { ProjectDetailHeaderComponent } from "@components/project-detail-header/project-detail-header.component";
import { FgdsProject } from "@domain/project/fgds-project";
import { MonitoringProject } from "@domain/project/monitoring-project";
import type { ScopedProject } from "@domain/project/project-guards";
import { isFgdsProject, isMonitoringProject } from "@domain/project/project-guards";
import { User } from "@domain/user/user";
import { UserService } from "@domain/user/user.service";
import { ChecklistMonitoringComponent } from "@project/project-details/checklist/checklist-monitoring.component";
import { ChecklistComponent } from "@project/project-details/checklist/checklist.component";
import { FilesComponent } from "@project/project-details/files/files.component";
import { FloorplanUploadComponent } from "@project/project-details/floorplan-upload/floorplan-upload.component";
import { ImageUploadComponent } from "@project/project-details/image-upload/image-upload.component";
import { MobileBottomNavComponent, MobileNavItem } from "@project/project-details/mobile-bottom-nav/mobile-bottom-nav.component";
import { MonitoringProductListComponent } from "@project/project-details/monitoring-product-list/monitoring-product-list.component";
import { ProductListComponent } from "@project/project-details/product-list/product-list.component";
import { ProjectCollaborationComponent } from "@project/project-details/project-collaboration/project-collaboration.component";
import { TabId } from "@project/project-details/project-details-tab-id";
import { ProjectInformationComponent } from "@project/project-details/project-information/project-information.component";
import { first, map, Observable } from "rxjs";
import { Breakpoints } from "../../../styles/breakpoints";

interface TabDef {
  id: TabId;
  label: string;
  testId: string;
  icon?: string;
  mobileTestId?: string;
  readOnlyHidden?: boolean;
}

@Component({
  selector: "app-project-details",
  templateUrl: "./project-details.component.html",
  styleUrls: ["./project-details.component.scss"],
  imports: [
    UiKitModule,
    ProjectDetailHeaderComponent,
    FloorplanUploadComponent,
    ImageUploadComponent,
    ProductListComponent,
    ProjectInformationComponent,
    ChecklistComponent,
    ChecklistMonitoringComponent,
    ProjectCollaborationComponent,
    FilesComponent,
    MonitoringProductListComponent,
    MobileBottomNavComponent,
  ],
  standalone: true,
})
export class ProjectDetailsComponent implements OnInit {
  private activeRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private userService = inject(UserService);
  private breakpointObserver = inject(BreakpointObserver);

  isMobile$ = this.breakpointObserver.observe(Breakpoints.mobile).pipe(map((result) => result.matches));

  protected project!: ScopedProject;
  protected fgdsProject?: FgdsProject;
  protected monitoringProject?: MonitoringProject;
  protected tabIndex: number = 0;
  protected isReadOnlyUser$: Observable<boolean>;

  protected hideBackArrow = false;

  protected desktopTabOrder: TabDef[] = [];
  protected mobileTabOrder: TabDef[] = [];

  private user!: User;

  readonly FGDS_DESKTOP_ORDER: TabId[] = [
    TabId.FLOORPLANS,
    TabId.IMAGES,
    TabId.FILES,
    TabId.PRODUCT_LIST,
    TabId.INFORMATION,
    TabId.COLLABORATION,
    TabId.CHECKLIST,
  ];
  readonly MONITORING_DESKTOP_ORDER: TabId[] = [
    TabId.INFORMATION,
    TabId.CHECKLIST,
    TabId.IMAGES,
    TabId.FLOORPLANS,
    TabId.FILES,
    TabId.COLLABORATION,
    TabId.PRODUCT_LIST,
  ];

  readonly FGDS_MOBILE_ORDER: TabId[] = [
    TabId.INFORMATION,
    TabId.CHECKLIST,
    TabId.IMAGES,
    TabId.PRODUCT_LIST,
    TabId.COLLABORATION,
    TabId.FILES,
  ];
  readonly MONITORING_MOBILE_ORDER: TabId[] = [
    TabId.INFORMATION,
    TabId.CHECKLIST,
    TabId.IMAGES,
    TabId.FILES,
    TabId.COLLABORATION,
    TabId.PRODUCT_LIST,
  ];

  readonly ALL_TABS: Record<TabId, TabDef> = {
    [TabId.FLOORPLANS]: {
      id: TabId.FLOORPLANS,
      label: $localize`:@@projectDetails.tabBar.tab1.name:Grundrisspläne`,
      testId: "projectDetails.tab.floorplans",
    },
    [TabId.IMAGES]: {
      id: TabId.IMAGES,
      label: $localize`:@@projectDetails.tabBar.tab2.name:Fotos`,
      testId: "projectDetails.tab.images",
      icon: "camera",
    },
    [TabId.FILES]: {
      id: TabId.FILES,
      label: $localize`:@@projectDetails.tabBar.tab7.name:Dateien`,
      testId: "projectDetails.tab.documents",
      mobileTestId: "projectDetails.tab.files",
      icon: "document",
      readOnlyHidden: true,
    },
    [TabId.PRODUCT_LIST]: {
      id: TabId.PRODUCT_LIST,
      label: $localize`:@@projectDetails.tabBar.tab3.name:Produktliste`,
      testId: "projectDetails.tab.productList",
      icon: "group",
    },
    [TabId.INFORMATION]: {
      id: TabId.INFORMATION,
      label: $localize`:@@projectDetails.tabBar.tab4.name:Projektinformationen`,
      testId: "projectDetails.tab.information",
      icon: "info",
    },
    [TabId.COLLABORATION]: {
      id: TabId.COLLABORATION,
      label: $localize`:@@projectDetails.tabBar.tab6.name:Kollaboration`,
      testId: "projectDetails.tab.collaboration",
      icon: "user-group",
      readOnlyHidden: true,
    },
    [TabId.CHECKLIST]: {
      id: TabId.CHECKLIST,
      label: $localize`:@@projectDetails.tabBar.tab5.name:Checkliste`,
      testId: "projectDetails.tab.checklist",
      icon: "document-check",
    },
  };

  protected get mobileNavItems(): MobileNavItem[] {
    return this.mobileTabOrder.map((tab) => ({
      id: tab.id,
      label: tab.label,
      icon: tab.icon ?? "",
      isReadOnly: tab.readOnlyHidden,
      testId: tab.mobileTestId ?? tab.testId,
    }));
  }

  protected get mobileVisibleTabOrder(): TabDef[] {
    return this.mobileTabOrder.filter((tab) => !(tab.readOnlyHidden && this.user?.readOnlyAccess));
  }

  constructor() {
    this.isReadOnlyUser$ = this.userService.user$.pipe(map((user) => user.readOnlyAccess));
  }

  ngOnInit(): void {
    this.project = this.activeRoute.snapshot.data["project"] as ScopedProject;
    this.project.setAllPositionIds();
    this.monitoringProject = isMonitoringProject(this.project) ? this.project : undefined;
    this.fgdsProject = isFgdsProject(this.project) ? this.project : undefined;

    this.userService.user$.pipe(first()).subscribe((user) => {
      this.user = user;
      if (user.readOnlyAccess) this.hideBackArrow = true;

      const desktopOrder = this.monitoringProject ? this.MONITORING_DESKTOP_ORDER : this.FGDS_DESKTOP_ORDER;
      this.desktopTabOrder = desktopOrder
        .map((tabId) => this.ALL_TABS[tabId])
        .filter((tab) => !(tab.readOnlyHidden && this.user.readOnlyAccess));

      const mobileOrder = this.monitoringProject ? this.MONITORING_MOBILE_ORDER : this.FGDS_MOBILE_ORDER;
      this.mobileTabOrder = mobileOrder.map((tabId) => this.ALL_TABS[tabId]);
    });
  }

  navigateBack() {
    this.router.navigate(["/"]);
  }
}
